import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { Field, FieldSet, TextField } from '@slal/ui/react-hook-form/fields';
import { normalizeNumbers } from '@slal/ui/react-hook-form/utils';
import { useNavigate } from 'react-router';
import { MaxWidthContent, Notification, SortCodeField, StyledForm } from '~/components';
import { useApplicationForm } from '~/hooks';
import { bankDetailsValidationSchema } from '~/schemas';
import { ApplicationFormKeys, BankDetailsSchema } from '~/types';
import { normalizeNumbersAndSlashes } from '~/utils';
import { ApplicationButtons } from '../ApplicationButtons';

export const BankDetailsForm = () => {
    const { handleSubmit, form } = useApplicationForm<BankDetailsSchema>(
        {
            defaultValues: { bankDetails: { buildingSocietyRollNumber: '', accountNumber: '' } },
            resolver: yupResolver(bankDetailsValidationSchema),
        },
        ApplicationFormKeys.BANK_DETAILS
    );
    const navigate = useNavigate();
    const onValidCallback = () => navigate('/application/review-and-confirm');
    const submitButtonLabel = 'Go to final checks';

    return (
        <MaxWidthContent>
            <Notification level="info">
                It's best to use a current account. Other account types take longer to verify, which can delay payment.
            </Notification>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback, submitButtonLabel)}>
                    <FieldSet aria-label="Bank details">
                        <Field label="Account holder name" labelFor="bankDetails.accountHolderName">
                            <TextField
                                name="bankDetails.accountHolderName"
                                id="bankDetails.accountHolderName"
                                maxLength={35}
                            />
                        </Field>

                        <Field label="Sort code" errorFor="bankDetails.sortCode">
                            <SortCodeField name="bankDetails.sortCode" />
                        </Field>

                        <Field label="Account number" labelFor="bankDetails.accountNumber">
                            <TextField
                                name="bankDetails.accountNumber"
                                id="bankDetails.accountNumber"
                                setValueAs={normalizeNumbers}
                                maxLength={8}
                            />
                        </Field>

                        <Field label="Bank name" labelFor="bankDetails.bankName">
                            <TextField name="bankDetails.bankName" id="bankDetails.bankName" maxLength={35} />
                        </Field>

                        <Field
                            label="Building society roll number"
                            labelFor="bankDetails.buildingSocietyRollNumber"
                            helpMessage="You need to provide this for certain building society accounts. You'll find it on your monthly statement."
                        >
                            <TextField
                                name="bankDetails.buildingSocietyRollNumber"
                                id="bankDetails.buildingSocietyRollNumber"
                                setValueAs={normalizeNumbersAndSlashes}
                            />
                        </Field>
                    </FieldSet>

                    <ApplicationButtons
                        backTo="/application/key-considerations/remaining-pension"
                        continueContent={submitButtonLabel}
                    />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};
